import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, query, where } from 'firebase/firestore';
import { FaClock, FaMagic, FaCalendarAlt, FaTrash, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';

export default function ServiceGenerator() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  const [name, setName] = useState('');
  const [dayOfWeek, setDayOfWeek] = useState('0');
  const [time, setTime] = useState('');

  const today = new Date();
  const [targetMonth, setTargetMonth] = useState(String(today.getMonth() + 1).padStart(2, '0'));
  const [targetYear, setTargetYear] = useState(String(today.getFullYear()));

  const [overwrite, setOverwrite] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const templatesRef = collection(db, "service_templates");
  const schedulesRef = collection(db, "schedules");
  const musiciansRef = collection(db, "musicians");
  const unavailRef = collection(db, "unavailability");
  const instrumentsRef = collection(db, "instruments");

  const daysMap = { 0: 'Domingo', 1: 'Segunda', 2: 'Terça', 3: 'Quarta', 4: 'Quinta', 5: 'Sexta', 6: 'Sábado' };
  const months = [{ val: '01', label: 'Janeiro' }, { val: '02', label: 'Fevereiro' }, { val: '03', label: 'Março' }, { val: '04', label: 'Abril' }, { val: '05', label: 'Maio' }, { val: '06', label: 'Junho' }, { val: '07', label: 'Julho' }, { val: '08', label: 'Agosto' }, { val: '09', label: 'Setembro' }, { val: '10', label: 'Outubro' }, { val: '11', label: 'Novembro' }, { val: '12', label: 'Dezembro' }];
  const years = [today.getFullYear(), today.getFullYear() + 1];

  const showFeedback = (msg, type = 'success') => {
    setFeedback({ msg, type });
    setTimeout(() => setFeedback(null), 4000);
  };

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await getDocs(templatesRef);
      const list = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      list.sort((a, b) => {
        if (a.dayOfWeek !== b.dayOfWeek) {
          return a.dayOfWeek - b.dayOfWeek;
        }
        const timeA = a.time || '00:00';
        const timeB = b.time || '00:00';
        return timeA.localeCompare(timeB);
      });
      setTemplates(list);
    } catch (error) { console.error("Erro ao carregar padrões:", error); }
    setLoading(false);
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddTemplate = async (e) => {
    e.preventDefault();
    if (!name || !time) return;
    try {
      await addDoc(templatesRef, { name, dayOfWeek: parseInt(dayOfWeek), time, createdAt: new Date() });
      setName(''); setTime(''); setDayOfWeek('0');
      loadTemplates();
      showFeedback("Padrão adicionado!");
    } catch (error) { console.error(error); }
  };

  const handleDeleteTemplate = async (id) => {
    if (window.confirm("Remover padrão?")) {
      await deleteDoc(doc(db, "service_templates", id));
      loadTemplates();
      showFeedback("Padrão removido.");
    }
  };

  // === GERADOR INTELIGENTE ===
  const handleGenerate = async () => {
    if (templates.length === 0) return showFeedback("Cadastre padrões primeiro.", "error");
    const confirmMsg = overwrite ? `ATENÇÃO: Isso APAGARÁ escalas de ${targetMonth}/${targetYear}.` : `Gerar para ${targetMonth}/${targetYear}?`;
    if (!window.confirm(confirmMsg)) return;

    setIsGenerating(true);
    try {
      // 1. Buscar Dados
      const musSnap = await getDocs(query(musiciansRef, where("isActive", "==", true)));
      const allMusicians = musSnap.docs.map(d => ({ ...d.data(), id: d.id }));
      const instSnap = await getDocs(instrumentsRef);
      const allInstruments = instSnap.docs.map(d => d.data().name);

      // 2. Indisponibilidades
      const startDateStr = `${targetYear}-${targetMonth}-01`;
      const lastDay = new Date(targetYear, parseInt(targetMonth), 0).getDate();
      const endDateStr = `${targetYear}-${targetMonth}-${lastDay}`;
      const unavailSnap = await getDocs(unavailRef);
      const unavailList = unavailSnap.docs.map(d => d.data()).filter(u => u.date >= startDateStr && u.date <= endDateStr);

      // 3. Loop
      const startDate = new Date(targetYear, parseInt(targetMonth) - 1, 1);
      const endDate = new Date(targetYear, targetMonth, 0);

      let count = 0; let skipped = 0; let replaced = 0;
      // Rastreador de Assiduidade POR FUNÇÃO (Round-Robin por instrumento)
      // Estrutura: { 'Bateria': { 'musicianId1': 2, 'musicianId2': 1 }, 'Ministro de Música': { ... } }
      const roleUsageCount = {};

      // Ordem: Ministro, Banda principal, depois Vocal (para agrupar sobras)
      const targetRoles = ['Ministro de Música', ...allInstruments.filter(i => i !== 'Ministro de Música' && i !== 'Vocal'), 'Vocal'];

      // Inicializa contadores zerados para cada função
      targetRoles.forEach(role => { roleUsageCount[role] = {}; });

      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const currentDayOfWeek = d.getDay();
        const servicesToday = templates.filter(t => t.dayOfWeek === currentDayOfWeek);

        for (let service of servicesToday) {
          const dateString = d.toISOString().split('T')[0];
          const q = query(schedulesRef, where("date", "==", dateString), where("title", "==", service.name), where("time", "==", service.time));
          const snapshot = await getDocs(q);

          if (!snapshot.empty) {
            if (overwrite) { await Promise.all(snapshot.docs.map(d => deleteDoc(doc(db, "schedules", d.id)))); replaced++; }
            else { skipped++; continue; }
          }

          const team = [];

          targetRoles.forEach(role => {
            // Filtra quem toca E está disponível
            let candidates = allMusicians.filter(m => {
              const playsInstrument = (Array.isArray(m.roles) && m.roles.includes(role)) || m.role === role;

              // Lógica de Indisponibilidade por Período
              const isUnavailable = unavailList.some(u => {
                if (u.musicianId !== m.id || u.date !== dateString) return false;

                // Se for domingo, checar o período
                if (currentDayOfWeek === 0) {
                  const isMorningService = service.time < "14:00";
                  const isEveningService = service.time >= "14:00";

                  if (u.servicePeriod === 'Ambos' || !u.servicePeriod) return true;
                  if (u.servicePeriod === 'Manhã' && isMorningService) return true;
                  if (u.servicePeriod === 'Noite' && isEveningService) return true;

                  return false;
                }

                return true;
              });

              return playsInstrument && !isUnavailable;
            });

            // === REGRA ESPECIAL: VOCAL (TODOS DISPONÍVEIS) ===
            if (role === 'Vocal') {
              candidates.forEach(vocalist => {
                if (!team.some(t => t.id === vocalist.id)) {
                  team.push({
                    id: vocalist.id,
                    name: vocalist.name,
                    role: role,
                    availableRoles: vocalist.roles || [vocalist.role]
                  });
                }
              });
              return;
            }

            // === REGRAS NORMAIS (1 POR INSTRUMENTO) ===
            const primaryCandidates = candidates.filter(m => m.primaryRole === role);
            const secondaryCandidates = candidates.filter(m => m.primaryRole !== role);

            let selected = null;

            // Função helper: escolhe o músico que MENOS tocou NAQUELA FUNÇÃO específica
            const getFairCandidate = (candidateList) => {
              const notYetSelected = candidateList.filter(c => !team.some(t => t.id === c.id));
              if (notYetSelected.length === 0) return null;

              // 1. Pega o contador desta função específica
              const countsForRole = roleUsageCount[role] || {};

              // 2. Descobre qual é o MENOR número de vezes que alguém tocou ESTA função
              let minCount = Infinity;
              notYetSelected.forEach(c => {
                const cnt = countsForRole[c.id] || 0;
                if (cnt < minCount) minCount = cnt;
              });

              // 3. Filtra SÓ quem tem esse número mínimo
              const pool = notYetSelected.filter(c => (countsForRole[c.id] || 0) === minCount);

              // 4. Sorteia apenas entre os empatados
              return pool[Math.floor(Math.random() * pool.length)];
            };

            if (primaryCandidates.length > 0) {
              selected = getFairCandidate(primaryCandidates);
            }

            if (!selected && secondaryCandidates.length > 0) {
              selected = getFairCandidate(secondaryCandidates);
            }

            if (selected) {
              team.push({ id: selected.id, name: selected.name, role: role, availableRoles: selected.roles || [selected.role] });
              // Incrementa o contador APENAS para esta função
              if (!roleUsageCount[role]) roleUsageCount[role] = {};
              roleUsageCount[role][selected.id] = (roleUsageCount[role][selected.id] || 0) + 1;
            }
          });

          await addDoc(schedulesRef, { title: service.name, date: dateString, time: service.time, team: team, repertoire: [], createdAt: new Date(), generated: true });
          count++;
        }
      }

      let msg = `Concluído! ${count} geradas.`;
      if (replaced) msg += ` (${replaced} substituídas)`;
      if (skipped) msg += ` (${skipped} puladas)`;
      showFeedback(msg);

    } catch (error) { console.error("Erro:", error); showFeedback("Erro técnico ao gerar.", "error"); }
    setIsGenerating(false);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {feedback && (<div className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 animate-fade-in-down ${feedback.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>{feedback.type === 'error' ? <FaExclamationCircle /> : <FaCheckCircle />}<span className="font-bold">{feedback.msg}</span></div>)}

      <h1 className="text-2xl font-bold text-gray-800 mb-6">Gerar Escalas</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* GERADOR */}
        <div>
          <div className="bg-brand-gold/10 p-6 rounded-lg shadow-md border-2 border-brand-gold border-dashed">
            <h3 className="text-lg font-bold mb-4 text-brand-blue flex items-center gap-2"><FaMagic /> 1. Gerar Automático</h3>
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700 mb-2">Mês Alvo</label>
              <div className="flex items-center bg-white border border-gray-300 rounded-lg px-3 py-2 shadow-sm">
                <FaCalendarAlt className="text-gray-400 mr-2" />
                <select value={targetMonth} onChange={(e) => setTargetMonth(e.target.value)} className="py-2 px-2 bg-transparent outline-none text-gray-700 text-sm cursor-pointer w-full">{months.map(m => <option key={m.val} value={m.val}>{m.label}</option>)}</select>
                <div className="text-gray-300 mx-2">|</div>
                <select value={targetYear} onChange={(e) => setTargetYear(e.target.value)} className="py-2 px-2 bg-transparent outline-none text-gray-700 text-sm cursor-pointer w-full">{years.map(y => <option key={y} value={y}>{y}</option>)}</select>
              </div>
            </div>
            <div className="mb-6 flex items-center gap-2"><input type="checkbox" id="overwrite" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} className="w-5 h-5 text-brand-blue rounded focus:ring-0 cursor-pointer" /><label htmlFor="overwrite" className="text-sm text-gray-700 font-bold cursor-pointer select-none">Sobrescrever escalas existentes? (Cuidado!)</label></div>
            <button onClick={handleGenerate} disabled={isGenerating || templates.length === 0} className={`w-full py-4 rounded-lg font-bold text-lg shadow-lg transition transform hover:scale-[1.02] ${isGenerating || templates.length === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-brand-gold text-brand-blue hover:bg-yellow-500'}`}>{isGenerating ? 'Gerando Inteligência...' : 'GERAR ESCALAS'}</button>
          </div>
        </div>

        {/* CADASTRO */}
        <div>
          <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-brand-blue mb-6">
            <h3 className="text-lg font-bold mb-4 text-gray-700 flex items-center gap-2"><FaClock /> 2. Configurar Padrões</h3>
            <form onSubmit={handleAddTemplate} className="space-y-4">
              <div><label className="block text-sm font-bold text-gray-600 mb-1">Nome do Culto/Evento</label><input value={name} onChange={e => setName(e.target.value)} className="w-full border p-2 rounded" placeholder="Ex: Culto da Família" required /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-bold text-gray-600 mb-1">Dia</label><select value={dayOfWeek} onChange={e => setDayOfWeek(e.target.value)} className="w-full border p-2 rounded bg-white">{Object.entries(daysMap).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
                <div><label className="block text-sm font-bold text-gray-600 mb-1">Horário</label><input type="time" value={time} onChange={e => setTime(e.target.value)} className="w-full border p-2 rounded" required /></div>
              </div>
              <button type="submit" className="w-full bg-brand-blue text-white font-bold py-2 rounded hover:bg-blue-800">Adicionar Padrão</button>
            </form>
          </div>
          <div className="space-y-2">
            {templates.map(t => (
              <div key={t.id} className="bg-white p-3 rounded shadow-sm flex justify-between items-center border-l-4 border-gray-300">
                <div><span className="font-bold text-gray-700 block">{t.name}</span><span className="text-sm text-gray-500">{daysMap[t.dayOfWeek]} às {t.time}h</span></div>
                <button onClick={() => handleDeleteTemplate(t.id)} className="text-red-400 hover:text-red-600 p-2"><FaTrash /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}